/**
 * PDF Region Selector Component
 * 
 * Displays a PDF preview and allows users to draw a bounding box
 * to define the region for BOM extraction.
 * 
 * Supports both server-side (Azure) and local (Tesseract.js) OCR.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useMsal } from '@azure/msal-react';

// Configure PDF.js worker - use local copy for reliability
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PdfRegionSelector = ({
  uploadId,
  filename,
  regionCoords,
  onRegionChange,
  templates,
  onApplyTemplate,
  onSaveTemplate,
  onGetPdfCanvas,  // Callback to provide PDF canvas for local OCR
  pdfFile  // Direct file object for local processing (alternative to uploadId)
}) => {
  const { instance, accounts } = useMsal();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfCanvasRef = useRef(null);  // Reference to the PDF rendered canvas
  
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  
  // Template save modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');

  // Load PDF URL (from server or direct file)
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // If pdfFile is provided directly (for local processing), use it
        if (pdfFile) {
          const objectUrl = URL.createObjectURL(pdfFile);
          setPdfUrl(objectUrl);
          setLoading(false);
          return;
        }
        
        // Otherwise, load from server
        if (!uploadId) {
          setLoading(false);
          return;
        }
        
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['api://lgl-tools-api/access_as_user'],
          account: accounts[0]
        });

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';
        const url = `${apiUrl}/bom-extraction/pdf-preview/${uploadId}`;
        
        // Create authenticated URL
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${tokenResponse.accessToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to load PDF');
        }
        
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
        
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (uploadId || pdfFile) {
      loadPdf();
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [uploadId, pdfFile, instance, accounts]);
  
  // Expose the PDF canvas for local OCR processing
  useEffect(() => {
    if (onGetPdfCanvas && pdfCanvasRef.current) {
      onGetPdfCanvas(() => pdfCanvasRef.current);
    }
  }, [onGetPdfCanvas, pageSize]);

  // Initialize region from props
  useEffect(() => {
    if (regionCoords && pageSize.width > 0) {
      setCurrentRect({
        x: (regionCoords.x / 100) * pageSize.width,
        y: (regionCoords.y / 100) * pageSize.height,
        width: (regionCoords.width / 100) * pageSize.width,
        height: (regionCoords.height / 100) * pageSize.height
      });
    }
  }, [regionCoords, pageSize]);

  // Draw rectangle on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || pageSize.width === 0) return;

    const ctx = canvas.getContext('2d');
    canvas.width = pageSize.width;
    canvas.height = pageSize.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (currentRect) {
      // Draw selection rectangle
      ctx.strokeStyle = '#007bff';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
      
      // Fill with semi-transparent color
      ctx.fillStyle = 'rgba(0, 123, 255, 0.1)';
      ctx.fillRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
      
      // Draw resize handles
      ctx.fillStyle = '#007bff';
      const handleSize = 8;
      const handles = [
        { x: currentRect.x, y: currentRect.y },
        { x: currentRect.x + currentRect.width, y: currentRect.y },
        { x: currentRect.x, y: currentRect.y + currentRect.height },
        { x: currentRect.x + currentRect.width, y: currentRect.y + currentRect.height }
      ];
      handles.forEach(h => {
        ctx.fillRect(h.x - handleSize/2, h.y - handleSize/2, handleSize, handleSize);
      });
    }
  }, [currentRect, pageSize]);

  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handlePageLoadSuccess = (page) => {
    const { width, height } = page;
    setPageSize({ width, height });
  };

  const getMousePosition = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleMouseDown = (e) => {
    const pos = getMousePosition(e);
    if (!pos) return;
    
    setIsDrawing(true);
    setStartPoint(pos);
    setCurrentRect(null);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !startPoint) return;
    
    const pos = getMousePosition(e);
    if (!pos) return;
    
    const rect = {
      x: Math.min(startPoint.x, pos.x),
      y: Math.min(startPoint.y, pos.y),
      width: Math.abs(pos.x - startPoint.x),
      height: Math.abs(pos.y - startPoint.y)
    };
    
    setCurrentRect(rect);
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentRect) {
      setIsDrawing(false);
      return;
    }
    
    setIsDrawing(false);
    
    // Convert to percentage coordinates
    if (pageSize.width > 0 && currentRect.width > 10 && currentRect.height > 10) {
      const coords = {
        x: (currentRect.x / pageSize.width) * 100,
        y: (currentRect.y / pageSize.height) * 100,
        width: (currentRect.width / pageSize.width) * 100,
        height: (currentRect.height / pageSize.height) * 100,
        page: currentPage
      };
      onRegionChange(coords);
    }
  };

  const handleClearSelection = () => {
    setCurrentRect(null);
    onRegionChange(null);
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim()) return;
    onSaveTemplate(templateName.trim(), templateDesc.trim());
    setShowSaveModal(false);
    setTemplateName('');
    setTemplateDesc('');
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner} />
          <p>Loading PDF preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <span style={styles.errorIcon}>⚠️</span>
          <p>Failed to load PDF: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h3 style={styles.title}>Define Extraction Region</h3>
          <p style={styles.filename}>{filename}</p>
        </div>
        
        <div style={styles.headerRight}>
          {templates && templates.length > 0 && (
            <select
              style={styles.templateSelect}
              onChange={(e) => {
                const template = templates.find(t => t.id === e.target.value);
                if (template) onApplyTemplate(template);
              }}
              defaultValue=""
            >
              <option value="" disabled>Apply Template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.template_name}</option>
              ))}
            </select>
          )}
          
          {currentRect && (
            <>
              <button
                style={styles.saveTemplateButton}
                onClick={() => setShowSaveModal(true)}
              >
                Save as Template
              </button>
              <button
                style={styles.clearButton}
                onClick={handleClearSelection}
              >
                Clear Selection
              </button>
            </>
          )}
        </div>
      </div>

      <div style={styles.instructions}>
        <span style={styles.instructionIcon}>💡</span>
        Click and drag on the PDF to draw a box around the Bill of Materials table.
        The same region will be extracted from all uploaded PDFs.
      </div>

      <div style={styles.pdfContainer} ref={containerRef}>
        <div style={styles.pdfWrapper}>
          {pdfUrl && (
            <Document
              file={pdfUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              loading={<div style={styles.pdfLoading}>Loading PDF...</div>}
            >
              <Page
                pageNumber={currentPage}
                onLoadSuccess={handlePageLoadSuccess}
                onRenderSuccess={() => {
                  // Capture the rendered canvas for local OCR
                  const pdfCanvas = document.querySelector('.react-pdf__Page__canvas');
                  if (pdfCanvas) {
                    pdfCanvasRef.current = pdfCanvas;
                  }
                }}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={800}
              />
            </Document>
          )}
          
          {pageSize.width > 0 && (
            <canvas
              ref={canvasRef}
              style={styles.canvas}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          )}
        </div>
        
        {numPages && numPages > 1 && (
          <div style={styles.pagination}>
            <button
              style={styles.pageButton}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              ← Previous
            </button>
            <span style={styles.pageInfo}>
              Page {currentPage} of {numPages}
            </span>
            <button
              style={styles.pageButton}
              onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
              disabled={currentPage >= numPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {currentRect && (
        <div style={styles.selectionInfo}>
          <strong>Selected Region:</strong>
          {' '}
          {Math.round(currentRect.width)}px × {Math.round(currentRect.height)}px
          {' '}
          at ({Math.round(currentRect.x)}, {Math.round(currentRect.y)})
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Save Region Template</h3>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Template Name *</label>
              <input
                type="text"
                style={styles.input}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Standard BOM Table"
                autoFocus
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Description (optional)</label>
              <textarea
                style={styles.textarea}
                value={templateDesc}
                onChange={(e) => setTemplateDesc(e.target.value)}
                placeholder="Describe when to use this template..."
                rows={3}
              />
            </div>
            
            <div style={styles.modalActions}>
              <button
                style={styles.cancelButton}
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.saveButton,
                  ...(templateName.trim() ? {} : styles.saveButtonDisabled)
                }}
                onClick={handleSaveTemplate}
                disabled={!templateName.trim()}
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px'
  },
  headerLeft: {
    flex: 1
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  title: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  filename: {
    margin: 0,
    fontSize: '13px',
    color: '#666'
  },
  templateSelect: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '13px',
    backgroundColor: 'white'
  },
  saveTemplateButton: {
    padding: '8px 12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer'
  },
  clearButton: {
    padding: '8px 12px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer'
  },
  instructions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#e7f3ff',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#0056b3'
  },
  instructionIcon: {
    fontSize: '16px'
  },
  pdfContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    backgroundColor: '#f5f5f5',
    borderRadius: '12px',
    overflow: 'auto'
  },
  pdfWrapper: {
    position: 'relative',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    backgroundColor: 'white'
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    cursor: 'crosshair'
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  pageButton: {
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer'
  },
  pageInfo: {
    fontSize: '13px',
    color: '#666'
  },
  selectionInfo: {
    padding: '12px 16px',
    backgroundColor: '#d4edda',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#155724'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#666'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #007bff',
    borderRadius: '50%',
    marginBottom: '16px',
    animation: 'spin 1s linear infinite'
  },
  pdfLoading: {
    padding: '60px',
    textAlign: 'center',
    color: '#666'
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '8px'
  },
  errorIcon: {
    fontSize: '24px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '400px',
    maxWidth: '90vw'
  },
  modalTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '20px'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f8f9fa',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  saveButtonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  }
};

export default PdfRegionSelector;

